﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
type SpecialFormMode = 'numeric' | 'alpha' | 'alphanumeric' | 'any';

export default class KDMZSpecialFormElement extends TKCustomElementFactory {
    private input: HTMLInputElement | null = null;
    private placeholder: HTMLSpanElement | null = null;
    private counter: HTMLSpanElement | null = null;
    private initialized: boolean = false;

    connectedCallback(): void {
        this.classList.add('special-form-element');

        this.input = this.querySelector('input');

        if (!this.input) {
            return;
        }

        this.init();
    }

    private init(): void {
        if (this.initialized) {
            return;
        }

        this.initialized = true;

        /*
         * Vorhandenes Input verwenden
         */
        this.input?.classList.add('special-form-element__input');

        /*
         * Placeholder
         */
        this.placeholder = document.createElement('span');

        this.placeholder.className = 'special-form-element__placeholder';

        this.placeholder.setAttribute('aria-hidden', 'true');

        this.insertBefore(this.placeholder, this.input);

        /*
         * Counter
         */
        this.configureCounter();

        this.bindEvents();
        this.configure();
        this.update();
    }

    private configure(): void {
        if (!this.input) {
            return;
        }

        const length = Number(this.input.dataset.length);

        /*
         * Bei IBAN wird maxLength nicht gesetzt,
         * da die Anzeige zusätzliche Leerzeichen enthält.
         *
         * data-length wird später auf den Rohwert
         * ohne Leerzeichen angewendet.
         */
        if (length > 0 && this.input.dataset.type !== 'iban') {
            this.input.maxLength = length;
        }

        /*
         * Input Mode
         */
        const mode = this.input.dataset.mode || 'any';

        if (mode === 'numeric') {
            this.input.setAttribute('inputmode', 'numeric');
        }
    }

    private configureCounter(): void {
        if (!this.input) {
            return;
        }

        /*
         * Counter wird nur erzeugt, wenn
         * data-counter-addon-text vorhanden ist.
         */
        if (!this.input.dataset.counterAddonText) {
            return;
        }

        const length = Number(this.input.dataset.length);

        if (length <= 0) {
            return;
        }

        this.counter = document.createElement('span');

        this.counter.className = 'special-form-element__counter';

        this.appendChild(this.counter);
    }

    private bindEvents(): void {
        if (!this.input) {
            return;
        }

        this.input.addEventListener('input', () => {
            this.handleInput();
        });

        this.input.addEventListener('focus', () => {
            this.classList.add('special-form-element--focused');
        });

        this.input.addEventListener('blur', () => {
            this.classList.remove('special-form-element--focused');
        });
    }

    private handleInput(): void {
        if (!this.input) {
            return;
        }

        /*
         * IBAN benötigt eine eigene Behandlung,
         * da sie während der Eingabe formatiert wird.
         */
        if (this.input.dataset.type === 'iban') {
            this.handleIbanInput();
            return;
        }

        /*
         * Bestehendes Verhalten für alle anderen Inputs.
         */
        let value = this.filterValue(this.input.value);

        const length = Number(this.input.dataset.length);

        if (length > 0 && value.length > length) {
            value = value.substring(0, length);
        }

        if (this.input.value !== value) {
            this.input.value = value;
        }

        this.update();

        requestAnimationFrame(() => {
            if (this.input) {
                this.input.scrollLeft = 0;
            }
        });
    }

    private handleIbanInput(): void {
        if (!this.input) {
            return;
        }

        /*
         * Cursorposition vor der Formatierung merken.
         */
        const cursorPosition = this.input.selectionStart ?? this.input.value.length;

        /*
         * Cursorposition im Rohwert bestimmen.
         *
         * Beispiel:
         *
         * CH93 0076|
         *
         * sichtbare Position: 9
         * Rohposition:        8
         */
        const rawCursorPosition = this.input.value.substring(0, cursorPosition).replace(/\s/g, '').length;

        /*
         * Leerzeichen entfernen.
         * Großschreibung erzwingen.
         * Nur A-Z und 0-9 erlauben.
         */
        let rawValue = this.input.value
            .replace(/\s/g, '')
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '');

        /*
         * data-length bezieht sich auf den Rohwert
         * OHNE Leerzeichen.
         */
        const length = Number(this.input.dataset.length);

        if (length > 0 && rawValue.length > length) {
            rawValue = rawValue.substring(0, length);
        }

        /*
         * Rohwert für die Anzeige formatieren.
         *
         * CH9300762011623852957
         *
         * =>
         *
         * CH93 0076 2011 6238 5295 7
         */
        const formattedValue = this.format(rawValue);

        this.input.value = formattedValue;

        /*
         * Cursorposition nach der Formatierung
         * wiederherstellen.
         */
        const newCursorPosition = this.getFormattedCursorPosition(
            Math.min(rawCursorPosition, rawValue.length),
            formattedValue,
        );

        requestAnimationFrame(() => {
            if (!this.input) {
                return;
            }

            this.input.setSelectionRange(newCursorPosition, newCursorPosition);

            this.input.scrollLeft = 0;
        });

        this.update();
    }

    private filterValue(value: string): string {
        if (!this.input) {
            return value;
        }

        const mode = this.input.dataset.mode || 'any';

        switch (mode as SpecialFormMode) {
            case 'numeric':
                return value.replace(/\D/g, '');

            case 'alpha':
                return value.replace(/[^a-zA-Z]/g, '');

            case 'alphanumeric':
                return value.replace(/[^a-zA-Z0-9]/g, '');

            default:
                return value;
        }
    }

    private format(value: string): string {
        if (!this.input) {
            return value;
        }

        const type = this.input.dataset.type || '';

        switch (type) {
            case 'iban':
                return value
                    .replace(/\s/g, '')
                    .toUpperCase()
                    .replace(/(.{4})/g, '$1 ')
                    .trim();

            default:
                return value;
        }
    }

    private getFormattedCursorPosition(rawPosition: number, formattedValue: string): number {
        /*
         * Für jeden vollständigen 4er-Block
         * kommt ein zusätzliches Leerzeichen hinzu.
         */
        let formattedPosition = rawPosition;

        for (let i = 4; i < rawPosition; i += 4) {
            formattedPosition++;
        }

        return Math.min(formattedPosition, formattedValue.length);
    }

    private getRawValue(): string {
        if (!this.input) {
            return '';
        }

        /*
         * IBAN:
         * Formatierungs-Leerzeichen entfernen.
         */
        if (this.input.dataset.type === 'iban') {
            return this.input.value.replace(/\s/g, '').toUpperCase();
        }

        /*
         * Alle anderen Felder unverändert.
         */
        return this.input.value;
    }

    private update(): void {
        this.updatePlaceholder();
        this.updateCounter();
    }

    private updatePlaceholder(): void {
        if (!this.input || !this.placeholder) {
            return;
        }

        const placeholder = this.input.dataset.placeholder || '';

        /*
         * VP Number und Worldline:
         * Placeholder bleibt als visuelle Vorlage sichtbar.
         */
        if (
            this.classList.contains('special-form-element--vp-number') ||
            this.classList.contains('special-form-element--worldline')
        ) {
            this.placeholder.textContent = placeholder;

            this.placeholder.classList.remove('special-form-element__placeholder--hidden');

            return;
        }

        /*
         * Alle anderen Felder:
         * Placeholder verschwindet bei Eingabe.
         */
        this.placeholder.textContent = placeholder;

        this.placeholder.classList.toggle('special-form-element__placeholder--hidden', this.input.value.length > 0);
    }

    private updateCounter(): void {
        if (!this.input || !this.counter) {
            return;
        }

        const maxLength = Number(this.input.dataset.length);
        const currentLength = this.getRawValue().length;
        const addonText = this.input.dataset.counterAddonText || '';

        this.counter.textContent = `${currentLength}/${maxLength} ${addonText}`;
    }
}
